import { motion } from "framer-motion";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { MapPin, Phone, Mail, Send } from "lucide-react";

export default function Contacto() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section id="contacto" ref={ref} className="py-28 md:py-40 px-6 lg:px-10 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1 }}
            className="text-accent text-sm tracking-[0.3em] uppercase font-light mb-6 block"
          >
            Contacto
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.1 }}
            className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground text-balance"
          >
            ¿Listo para vivir la experiencia?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1.2, delay: 0.2 }}
            className="text-foreground/60 font-light text-lg mt-6 max-w-2xl mx-auto"
          >
            Contáctanos para reservar tu estancia o resolver cualquier duda.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Información de contacto */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.3 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <h3 className="font-serif-display text-2xl text-foreground font-light">
                Información de Contacto
              </h3>
              <p className="text-foreground/70 font-light">
                Estamos aquí para ayudarte. Contáctanos por cualquiera de estos medios:
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="font-light text-foreground/80">Dirección</p>
                  <p className="text-sm text-foreground/60 font-light">
                    Fresnos No.64 Colonia el Sauz<br />
                    Tequisquiapan, Querétaro CP 76796
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="font-light text-foreground/80">Teléfonos</p>
                  <p className="text-sm text-foreground/60 font-light">
                    <a href="tel:+524142196366" className="hover:text-accent transition-colors">
                      (414) 219 63 66
                    </a>
                    <br />
                    <a href="tel:+524271143542" className="hover:text-accent transition-colors">
                      (427) 114 35 42
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="font-light text-foreground/80">Correo Electrónico</p>
                  <p className="text-sm text-foreground/60 font-light">
                    <a href="mailto:reservaciones@casaelgavillero.com.mx" className="hover:text-accent transition-colors">
                      reservaciones@casaelgavillero.com.mx
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Formulario con mailto: */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.4 }}
          >
            <form 
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.target;
                const nombre = form.nombre.value;
                const correo = form.correo.value;
                const telefono = form.telefono.value;
                const mensaje = form.mensaje.value;
                
                const subject = encodeURIComponent(`Consulta de ${nombre} - Casa El Gavillero`);
                const body = encodeURIComponent(
                  `Nombre: ${nombre}\n` +
                  `Correo: ${correo}\n` +
                  `Teléfono: ${telefono || 'No especificado'}\n\n` +
                  `Mensaje:\n${mensaje}\n\n` +
                  `---\n` +
                  `Este mensaje fue enviado desde el sitio web de Casa El Gavillero.`
                );
                
                window.location.href = `mailto:reservaciones@casaelgavillero.com.mx?subject=${subject}&body=${body}`;
              }}
            >
              <div>
                <label htmlFor="nombre" className="block text-sm font-light text-foreground/70 mb-1">
                  Nombre completo *
                </label>
                <input
                  id="nombre"
                  name="nombre"
                  type="text"
                  required
                  className="w-full px-4 py-3 bg-secondary/30 border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-accent text-foreground font-light transition-all"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label htmlFor="correo" className="block text-sm font-light text-foreground/70 mb-1">
                  Correo electrónico *
                </label>
                <input
                  id="correo"
                  name="correo"
                  type="email"
                  required
                  className="w-full px-4 py-3 bg-secondary/30 border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-accent text-foreground font-light transition-all"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label htmlFor="telefono" className="block text-sm font-light text-foreground/70 mb-1">
                  Teléfono
                </label>
                <input
                  id="telefono"
                  name="telefono"
                  type="tel"
                  className="w-full px-4 py-3 bg-secondary/30 border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-accent text-foreground font-light transition-all"
                  placeholder="(414) 219 63 66"
                />
              </div>

              <div>
                <label htmlFor="mensaje" className="block text-sm font-light text-foreground/70 mb-1">
                  Mensaje *
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  rows={4}
                  required
                  className="w-full px-4 py-3 bg-secondary/30 border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-accent text-foreground font-light transition-all resize-none"
                  placeholder="Cuéntanos sobre tu estancia ideal..."
                />
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-accent text-white font-light tracking-wide hover:bg-accent/80 transition-colors"
              >
                <Send className="w-4 h-4" />
                Enviar por correo
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}